import { QueryStatus } from "@reduxjs/toolkit/query";
import { usePostHog } from "@posthog/react";
import { AxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { RequestError, useEnterCheckpointMutation } from "../../api";
import { posApiInstance } from "../../api/axios/api-instances";
import { refreshAccessToken } from "../../api/helpers/refresh-token";
import { appQRSchema } from "../../components/payment/payment-qr-scan";
import { getFullDepositAmount } from "../../shared/lib/helpers";
import { useKioskBusy } from "../../shared/lib/kiosk-activity";
import { appSession } from "../../shared/lib/storage";
import { QRScanner } from "../autonomous/qr-scanner";
import { CheckoutModal } from "./checkout-modal/checkout-modal";
import { getHttpStatus, isAbortError } from "./request-failure";
import { depositSuccessSound, errorSound } from "./sounds";
import type {
  KioskPermissionDeniedContext,
  KioskSessionInvalidContext,
  KioskStreamFailureContext,
  RefreshKioskSession,
} from "./types";
import { UnmannedEnterInstructions } from "./unmanned-enter-instructions";
import { useManualSse } from "./use-manual-sse";
import {
  isInfrastructureFailure,
  type OperationCircuitController,
} from "./use-operation-circuit";

type ScanSource = "sse" | "scanner";

type UmannedEnterProps = {
  storeKey: string;
  zoneId: string;
  refreshSession?: RefreshKioskSession;
  onPermissionDenied: (context: KioskPermissionDeniedContext) => void;
  onSessionInvalid: (context: KioskSessionInvalidContext) => void;
  operationCircuit: OperationCircuitController;
  onStreamUnavailable: (context: KioskStreamFailureContext) => void;
  onStreamRecovered: () => void;
};

export function UmannedEnter({
  storeKey,
  zoneId,
  refreshSession,
  onPermissionDenied,
  onSessionInvalid,
  operationCircuit,
  onStreamUnavailable,
  onStreamRecovered,
}: UmannedEnterProps) {
  const posthog = usePostHog();
  const [open, setOpen] = useState(false);
  const [enterCheckpoint] = useEnterCheckpointMutation();
  const { t } = useTranslation();
  const defaultErrorMessage = t("paymentFailedDescription");
  const [status, setStatus] = useState<QueryStatus>(QueryStatus.uninitialized);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [depositedAmount, setDepositedAmount] = useState("");
  const isScanDisabled = useRef(false);
  const checkpointRequest = useRef<{ abort: () => void } | null>(null);
  const {
    canAttempt: canAttemptCheckpoint,
    recordFailure: recordCheckpointFailure,
    recordSuccess: recordCheckpointSuccess,
  } = operationCircuit;

  useKioskBusy(open);

  useEffect(() => {
    posthog.register({
      zone_id: zoneId,
      store_key: storeKey,
      sse_transport: "xhr_manual_reconnect",
      ...(import.meta.env.VITE_APP_VERSION
        ? { app_version: import.meta.env.VITE_APP_VERSION }
        : {}),
    });
    posthog.capture("unmanned_enter_viewed", {
      zone_id: zoneId,
      store_key: storeKey,
    });
  }, [posthog, storeKey, zoneId]);

  useEffect(
    () => () => {
      checkpointRequest.current?.abort();
      checkpointRequest.current = null;
      isScanDisabled.current = false;
    },
    [],
  );

  const capture = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      posthog.capture(event, properties);
    },
    [posthog],
  );

  const parseEventData = useCallback((eventData: unknown) => {
    try {
      const payload = appQRSchema.parse(
        JSON.parse(typeof eventData === "string" ? eventData : ""),
      );
      return { payload, success: true } as const;
    } catch {
      return { payload: null, success: false } as const;
    }
  }, []);

  const validateEventData = useCallback(
    (event: MessageEvent) => {
      const { payload, success } = parseEventData(event.data);
      if (success && payload) return payload;

      setStatus(QueryStatus.rejected);
      setErrorMessage("Cannot parse scanned image. Invalid QR code");
      setOpen(true);
      posthog.capture("invalid_qr_code_scanned", {
        zone_id: zoneId,
        source: "sse" satisfies ScanSource,
      });
      return null;
    },
    [parseEventData, posthog, zoneId],
  );

  const makeCheckpoint = useCallback(
    async (
      payload: z.infer<typeof appQRSchema>,
      source: ScanSource = "sse",
    ) => {
      if (isScanDisabled.current || !canAttemptCheckpoint) return;

      const startedAt = Date.now();
      setOpen(true);
      setStatus(QueryStatus.pending);
      setDepositedAmount("");
      isScanDisabled.current = true;
      posthog.capture("checkpoint_enter_started", { zone_id: zoneId, source });

      const request = enterCheckpoint({ ...payload, zone_id: zoneId });
      checkpointRequest.current = request;

      try {
        const checkpointResult = await request.unwrap();
        recordCheckpointSuccess();
        posthog.capture("enter_checkpoint", {
          ...checkpointResult.result,
          zone_id: zoneId,
          source,
          duration_ms: Date.now() - startedAt,
        });

        if (checkpointResult.result.success) {
          setStatus(QueryStatus.fulfilled);
          setDepositedAmount(getFullDepositAmount(checkpointResult));
          depositSuccessSound.play();
        } else {
          setStatus(QueryStatus.rejected);
          setErrorMessage(checkpointResult.result.message || defaultErrorMessage);
          errorSound.play();
        }
      } catch (error) {
        // The kiosk aborts this request in two places, and neither is an
        // outage: the customer dismissing the result dialog, and the unmount
        // cleanup. An abort carries no HTTP status, so it would otherwise
        // classify as an infrastructure failure — playing the error sound at a
        // customer who just closed the dialog, and pushing the shared circuit
        // toward opening on three dismissals inside two minutes. There is no
        // deadline abort on this operation, so every abort here is deliberate.
        if (isAbortError(error)) {
          posthog.capture("checkpoint_enter_cancelled", {
            zone_id: zoneId,
            source,
            duration_ms: Date.now() - startedAt,
          });
          return;
        }
        const httpStatus = getHttpStatus(error);
        const axiosError = error as AxiosError<RequestError>;
        posthog.capture("checkpoint_enter_failed", {
          zone_id: zoneId,
          source,
          http_status: httpStatus,
          duration_ms: Date.now() - startedAt,
          message: axiosError.response?.data?.errors?.[0]?.msg,
        });
        posthog.captureException(error);
        if (httpStatus === 403) {
          onPermissionDenied({ operation: "checkpoint_enter", status: 403 });
        } else if (isInfrastructureFailure(httpStatus)) {
          recordCheckpointFailure();
        }
        setStatus(QueryStatus.rejected);
        setErrorMessage(
          axiosError.response?.data?.errors?.[0]?.msg || defaultErrorMessage,
        );
        errorSound.play();
      } finally {
        checkpointRequest.current = null;
      }
    },
    [
      defaultErrorMessage,
      canAttemptCheckpoint,
      enterCheckpoint,
      onPermissionDenied,
      posthog,
      recordCheckpointFailure,
      recordCheckpointSuccess,
      zoneId,
    ],
  );

  const handleScan = useCallback(
    (event: MessageEvent) => {
      if (isScanDisabled.current) {
        posthog.capture("sse_scan_ignored", {
          zone_id: zoneId,
          reason: "scan_in_progress",
        });
        return;
      }
      posthog.capture("sse_scan_received", {
        zone_id: zoneId,
        data_length: typeof event.data === "string" ? event.data.length : 0,
      });
      const payload = validateEventData(event);
      if (payload) void makeCheckpoint(payload);
    },
    [makeCheckpoint, posthog, validateEventData, zoneId],
  );

  const refreshScanSession = useCallback(async () => {
    if (refreshSession) {
      await refreshSession(storeKey);
      return;
    }
    await refreshAccessToken({ storeKey, reason: "sse-401" });
  }, [refreshSession, storeKey]);

  const getAccessToken = useCallback(
    () => appSession.getItem()?.access_token,
    [],
  );

  useManualSse({
    url: posApiInstance.defaults.baseURL
      ? `${posApiInstance.defaults.baseURL}/zones/${zoneId}/scan/stream`
      : undefined,
    zoneId,
    getAccessToken,
    refreshSession: refreshScanSession,
    onScan: handleScan,
    onPermissionDenied,
    onSessionInvalid,
    onUnavailable: onStreamUnavailable,
    onRecovered: onStreamRecovered,
    capture,
  });

  const onDebugScan = (payload: z.infer<typeof appQRSchema>) => {
    if (!import.meta.env.PROD) void makeCheckpoint(payload, "scanner");
  };

  return (
    <>
      <div className="bg-white p-6 h-screen mobile-fa">
        <UnmannedEnterInstructions />
        <QRScanner<z.infer<typeof appQRSchema>>
          onScanned={onDebugScan}
          schema={appQRSchema}
        />
      </div>
      <CheckoutModal
        open={open}
        status={status}
        errorMessage={errorMessage}
        setOpen={(openState) => {
          if (!openState) {
            checkpointRequest.current?.abort();
            checkpointRequest.current = null;
            isScanDisabled.current = false;
            posthog.capture("scan_lock_released", { zone_id: zoneId });
          }
          setOpen(openState);
        }}
        amount={depositedAmount}
      />
    </>
  );
}
